import { Actie, BaseActie, useDeclineActie } from "@/entities/actie";
import { OpstartplanActieDialog, PrognoseActieDialog, TrajectenOfReikwijdteGewijzigdActieDialog, VsmActieDialog } from "@/features/acties";
import { ConfirmDialog, Dialog, DialogContainer, DialogDescription, DialogHeader, DialogTitle, Input, Label } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { RadixBaseUiInteropContextProvider } from "@/shared/components/utils/radix-base-ui-interop-provider.tsx";
import { useIsScrolled } from "@/shared/hooks";
import { cn } from "@/shared/utils";
import { ComponentType, PropsWithChildren, useRef, useState } from "react";

type ProcessActieDialogProps<T extends Actie> = ActieDialogProps<T> & {
    className?: string;
    footer: ComponentType<ActieDialogFooterProps<T>>;
};

type ActieDialogProps<T extends Actie> = {
    actie: T;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dossierId: string;
};

export function ActieDialog<T extends Actie>({
    open,
    onOpenChange,
    actie,
    dossierId,
    children,
    className,
    footer: FooterComponent,
}: Readonly<PropsWithChildren<ProcessActieDialogProps<T>>>) {
    const radixRef = useRef<HTMLDivElement | null>(null);
    const { ref, hasVerticalScrollbar } = useIsScrolled();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <RadixBaseUiInteropContextProvider radixElement={radixRef}>
                <DialogContainer
                    ref={radixRef}
                    data-testid={`actie-dialog-${actie.type.toLowerCase()}`}
                    className={cn("flex min-h-[400px] min-w-[640px] flex-col overflow-hidden p-0", className)}
                    onInteractOutside={(e) => {
                        // Allow clicking on toasts without closing dialog
                        const target = e.target as HTMLElement;
                        if (target.closest("[data-sonner-toast]")) {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader className={cn("bg-background sticky top-0 px-6 pt-6")}>
                        <DialogTitle>{actie.dialogContent?.titel}</DialogTitle>
                        <DialogDescription className="sr-only">{actie.dialogContent?.beschrijving}</DialogDescription>
                    </DialogHeader>

                    <div ref={ref} className={cn("gap-m flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-6 pb-4")}>
                        <p data-testid="dialog-actie-description" className="text-content-default leading-l text-m">
                            {actie.dialogContent?.beschrijving}
                        </p>
                        {children}
                    </div>

                    <FooterComponent
                        className={cn("bg-background shadow-elevated-2-inverted sticky top-0", hasVerticalScrollbar && "pt-3")}
                        dossierId={dossierId}
                        actie={actie}
                        onClose={() => onOpenChange(false)}
                    />
                </DialogContainer>
            </RadixBaseUiInteropContextProvider>
        </Dialog>
    );
}

export type ActieDialogFooterProps<T extends Actie> = {
    dossierId: string;
    actie: T;
    onClose: () => void;
    className?: string;
    toelichting?: string;
};

type ActieDialogToelichtingProps = {
    toelichtingLabel?: string;
    toelichting: string;
    setToelichting: (toelichting: string) => void;
    showToelichtingHelpText?: boolean;
    className?: string;
};

function ActieDialogToelichting({
    toelichtingLabel = "Toelichting",
    toelichting,
    setToelichting,
    showToelichtingHelpText = true,
    className,
}: Readonly<ActieDialogToelichtingProps>) {
    return (
        <div className={className}>
            <Label htmlFor="toelichting" className="leading-l text-s flex justify-between font-bold">
                <div>
                    {toelichtingLabel}&nbsp;
                    <span className="leading-l text-xs font-normal">(Optioneel)</span>
                </div>
            </Label>
            <Input
                data-testid="actie-dialog-toelichting"
                id="toelichting"
                value={toelichting}
                onChange={(e) => setToelichting(e.target.value)}
            />
            {showToelichtingHelpText && (
                <span className="text-content-subtle leading-l text-s font-normal">
                    Toelichtingen worden na bevestigen aan medewerkers in Wissel getoond
                </span>
            )}
        </div>
    );
}

type DeclineActieConfirmationProps = {
    actie: BaseActie;
    dossierId: string;
    closeActieDialog: () => void;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
};

export function DeclineActieConfirmation({
    actie,
    dossierId,
    closeActieDialog,
    isOpen,
    setIsOpen,
}: Readonly<DeclineActieConfirmationProps>) {
    const [error, setError] = useState<Error | null>(null);
    const declineActie = useDeclineActie();

    const handleDeclineAction = async () => {
        const result = await declineActie.mutateAsync({ dossierId, actieId: actie.id });
        if (result) {
            setIsOpen(false);
            closeActieDialog();
        } else {
            setError(new Error("Er was een fout bij het weigeren van de actie"));
        }
    };

    const handleCancel = () => {
        setIsOpen(false);
    };

    return (
        <ConfirmDialog
            destructive
            isOpen={isOpen}
            title={actie.dialogContent?.weigerenTitel}
            confirmLabel={actie.dialogContent?.weigerenActieTekst}
            onCancel={handleCancel}
            onConfirm={handleDeclineAction}
            async
        >
            <span>{actie.dialogContent?.weigerenBeschrijving}</span>
            {error && <ErrorDisplay error={error} />}
        </ConfirmDialog>
    );
}

function ActieDialogFactory<T extends Actie>({ actie, ...props }: Readonly<ActieDialogProps<T>>) {
    switch (actie.type) {
        case "Prognose":
            return <PrognoseActieDialog actie={actie} {...props} />;
        case "Opstartplan":
            return <OpstartplanActieDialog actie={actie} {...props} />;
        case "Vsm":
            return <VsmActieDialog actie={actie} {...props} />;
        case "TrajectenGewijzigd":
        case "ReikwijdteGewijzigd":
            return <TrajectenOfReikwijdteGewijzigdActieDialog actie={actie} {...props} />;
        default:
            return null;
    }
}

ActieDialog.Factory = ActieDialogFactory;
ActieDialog.Toelichting = ActieDialogToelichting;
